import React, { useContext, useEffect, useState } from 'react';

import { WarehouseInventoryV2Api } from 'api/warehouseInventoryV2';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

import { ActionModal } from './ActionModal';
import { ActionsCell } from './ActionsCell';
import { CircularProgressOverlay } from './CircularProgressOverlay';
import { EmptyPalletCreator } from './emptyPalletCreator';
import { LocationTabExpansion } from './locationTabExpansion';
import { useInventorySearchValues } from './useInventorySearchValues';
import { BulkMovePalletsAction } from './actions/bulkMovePallets/BulkMovePalletsAction';
import { EntireLocationEmptyAction } from './actions/entireLocationEmptyAction';

const warehouseInventoryV2 = new WarehouseInventoryV2Api();

function LocationTab({ onLinkClicked, queryParams }) {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [rows, setRows] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [viewActions, setViewActions] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const { roles, user } = useContext(UserContext);
    const userCanOffsite = roles?.some((role) => role.name === 'OFFSITE_TRANSFERS');
    const [locationCanBulkMove, setLocationCanBulkMove] = useState(false);
    const searchValues = useInventorySearchValues(queryParams);

    // search bar queryParams effect
    useEffect(() => {
        handleSearch();
    }, [queryParams]);

    const handleSearch = async () => {
        // TODO handle the filtering of the row data to match queryPrams
        // TODO read and verify query params to pass to api call
        getLocations();
    };

    // Column Definitions
    const columns = [
        { Header: 'Location', accessor: 'locationName' },
        { Header: 'Active', accessor: 'active', type: 'checkbox' },
        { Header: 'Unique SKUs', accessor: 'uniqueSkus' },
        { Header: 'Lots', accessor: 'lots' },
        { Header: 'Pallets', accessor: 'pallets' },
        { Header: 'Cases', accessor: 'cases' },
        { Header: 'Loose Units', accessor: 'looseUnits' },
        { Header: 'Total Units', accessor: 'totalUnits' },
        {
            accessor: 'actions',
            Header: 'Actions',
            Cell: ({ row }) => (
                <ActionsCell row={row} setSelectedRow={setSelectedRow} setViewActions={setViewActions} />
            ),
        },
    ];

    // API call to GET data
    const getLocations = async () => {
        setLocationsLoading(true);

        // Include empty locations if no filters
        const includeEmptyLocations = !searchValues.filter((item) => item.searchBy !== undefined).length;

        let locationResult = [];
        if (includeEmptyLocations) {
            const promises = [
                warehouseInventoryV2.getLocations(user.facilityID, searchValues),
                warehouseInventoryV2.getEmptyLocations(user.facilityID),
            ];
            const result = await Promise.all(promises);
            if (result.every((item) => item.status)) {
                locationResult = [...result[0].data.locations, ...result[1].data.locations];
            } else {
                setNotification({ text: 'Failed to get locations', level: 'error' });
                setLocationsLoading(false);
                return;
            }
        } else {
            const result = await warehouseInventoryV2.getLocations(user.facilityID, searchValues);
            if (!result.status) {
                setNotification({ text: result.msg, level: 'error' });
                setLocationsLoading(false);
                return;
            }
            locationResult = result.data.locations;
        }

        setRows(
            locationResult.map((location) => ({
                ...location,
                locationName: location.locationName,
                active: location.numLots > 0,
                uniqueSkus: location.numSku,
                lots: location.numLots,
                pallets: location.numPallets,
                cases: location.numCases,
                looseUnits: location.numLooseUnits,
                totalUnits: location.numTotalUnits,
                locationId: location.locationId,
                locInvType: location.inventoryType,
                enableBulkMove: location.enableBulkMove ?? false,
            })),
        );

        setLocationsLoading(false);
    };

    const handleOnRowClicked = (row) => {
        setSelectedRow(row.original);
    };

    const handleModalClose = () => {
        setViewActions(false);
    };

    const handleSuccess = (message = 'Success') => {
        setNotification({ text: message, level: 'success' });
        handleModalClose();
        getLocations();
    };

    // Action Definitions
    const baseActions = [
        {
            text: 'Entire Location Empty',
            value: 'entireLocationEmpty',
            componentJSX() {
                return (
                    <EntireLocationEmptyAction
                        locationId={selectedRow?.locationId}
                        onSuccess={handleSuccess}
                        onCancel={handleModalClose}
                    />
                );
            },
        },
        {
            text: 'Create Empty Pallet',
            value: 'createEmptyPallet',
            componentJSX() {
                return <EmptyPalletCreator locationId={selectedRow?.locationId} />;
            },
        },
    ];

    const bulkMoveAction = {
        text: 'Bulk Move Pallets',
        value: 'bulkMovePallets',
        componentJSX() {
            return (
                <BulkMovePalletsAction
                    locationId={selectedRow?.locationId}
                    locationName={selectedRow?.locationName}
                    onSuccess={handleSuccess}
                    onCancel={handleModalClose}
                />
            );
        },
    };

    const actions = [...baseActions, ...(locationCanBulkMove ? [bulkMoveAction] : [])];

    useEffect(() => {
        if (userCanOffsite && selectedRow?.enableBulkMove) {
            setLocationCanBulkMove(true);
        } else {
            setLocationCanBulkMove(false);
        }
    }, [selectedRow, userCanOffsite]);

    return (
        <>
            <Notifications options={notification} />
            <CircularProgressOverlay loading={locationsLoading} />
            <DataTable
                csvExport
                csvExportFileName="location_inventory"
                columns={columns}
                data={rows}
                editable={false}
                tableContainerMaxHeight={800}
                stickyHeader
                expandable
                initialRowsPerPage={100}
                ExpansionComponent={
                    <LocationTabExpansion
                        searchValues={searchValues}
                        locationName={selectedRow?.locationName}
                        locationId={selectedRow?.locationId}
                        enableBulkMove={selectedRow?.enableBulkMove}
                        locInvType={selectedRow?.locInvType}
                        onLinkClicked={onLinkClicked}
                        onSuccess={handleSuccess}
                    />
                }
                onRowClicked={handleOnRowClicked}
            />
            <ActionModal
                title={`Actions for Location ${selectedRow.locationName}`}
                open={viewActions}
                row={selectedRow}
                actions={actions}
                closeModal={handleModalClose}
            />
        </>
    );
}

export { LocationTab };
